// Drugs' Molecule section (ba-web; the owner's discovery audit, Q2(a)(iii)): the run's primary target as a measured
// structure from the RCSB PDB, drawn beside the brain through the viewport's `mesh` command. The data is
// pipelines/pharma-structures' compact set at sims/pharma/structures-v1 (schema brain-atlas.pharma-structures.v1,
// provenance reference): each entry's Cα trace with its secondary structure, UniProt segments and gaps, and the bound
// drug's heavy atoms and bonds. The target's chains draw as tubes coloured by secondary structure, the other chains
// (G proteins, antibody fragments, the crystal constructs' T4 lysozyme and BRIL fusions) as one dim translucent tube,
// the drug in cyan and co-ligands (GABA, glycine, glutamate) in green, the whole standing on its longest axis a hand's
// breadth left of the brain at MM_PER_ANGSTROM: an illustrative placement and scale, the structure itself the entry's
// own coordinates. The 2D preview has no `mesh` command, so there the panel says the molecule needs the 3D viewport.

import { create } from 'zustand';
import { defaultSource, SimFiles } from '../data/sims/files.ts';
import type { Command, Orbit, Viewport } from '../viewport/api.ts';
import { drawsMeshes, type LiveViewport } from './stim.ts';
import { bounds, chainTubes, ligandGeometry, merge, principalAxes, transformPoint, uprightPose, type Geometry, type TubeClass, type Vec3 } from './moleculeGeometry.ts';

export const STRUCTURES_DIR = 'pharma/structures-v1';
export const STRUCTURES_SCHEMA = 'brain-atlas.pharma-structures.v1';
/** The section's scale: 1 Å drawn as 0.5 mm, five million times life size. */
export const MM_PER_ANGSTROM = 0.5;
/** Where the molecule stands, RAS mm: left of the template brain (half width about 72 mm) by a gap, a little forward and up. */
export const BRAIN_HALF_WIDTH_MM = 72;
export const GAP_MM = 18;
export const PLACE_Y_MM = 20;
export const PLACE_Z_MM = 10;
export const MESH_PREFIX = 'molecule:';
/** The meshes' colours: the target's secondary structure, the other chains, the drug and the co-ligands. */
export const LOOK: Record<TubeClass | 'drug' | 'ligand', string> = { helix: '#E8735A', strand: '#F0C75E', coil: '#9AA6B8', other: '#6B7480', drug: '#4FD1E8', ligand: '#7AD37A' };
export const OTHER_OPACITY = 0.35;

export interface StructureRecord {
  id: string;
  title: string;
  target: string;
  role: string;
  provenance: string;
  source_url: string;
  pdb_doi: string;
  method: string;
  resolution_a: number | null;
  citation?: { title?: string; rcsb_authors?: string[]; year?: number; pdbx_database_id_DOI?: string; rcsb_journal_abbrev?: string; journal_abbrev?: string } | null;
  ligand: { ccd: string; name: string; ccd_name: string | null; drug: string | null; matches_drug: boolean; copies: number };
  serves: string[];
  uniprot: string[];
  target_chains: string[];
  chains: { id: string; entity: string | null; residues: number; target: boolean }[];
  other_ligands: string[];
  path: string;
  bytes: number;
  sha256: string;
  note?: string;
}
export interface StructuresManifest {
  schema: string;
  created: string;
  provenance: string;
  structures: StructureRecord[];
}
export interface StructureChain {
  id: string;
  label_asym_id: string;
  entity: string | null;
  target: boolean;
  ca: Vec3[];
  residues: { seq: number; auth: number | string | null; name: string }[];
  ss: { kind: 'helix' | 'strand'; from: number; to: number }[];
  segments: { from: number; to: number; db: string | null; code: string | null; accession: string | null }[];
  gaps: number[];
}
export interface StructureLigand {
  ccd: string;
  name: string | null;
  chain: string;
  label_asym_id: string;
  role: 'drug' | 'GABA' | 'other';
  atoms: { el: string; name: string; x: number; y: number; z: number }[];
  bonds: [number, number][];
}
export interface StructureFile {
  schema: string;
  id: string;
  title: string;
  method: string;
  resolution_a: number | null;
  citation: { title: string | null; authors: string[]; journal: string | null; year: number | null; doi: string | null; pubmed: number | null };
  target: string;
  provenance: string;
  space: string;
  chains: StructureChain[];
  ligands: StructureLigand[];
  centre: Vec3 | null;
  extent_a: Vec3 | null;
  models_in_file: number;
}

/** The manifest, or a thrown error naming what it isn't. */
export function parseManifest(x: unknown): StructuresManifest {
  const m = x as Partial<StructuresManifest> | null;
  if (!m || typeof m !== 'object' || m.schema !== STRUCTURES_SCHEMA || !Array.isArray(m.structures)) throw new Error(`structures.json isn’t a ${STRUCTURES_SCHEMA} manifest`);
  return m as StructuresManifest;
}

/** The structure for a run: the one whose `serves` names the run, else the first for the run's primary target with a
 *  drug of the app's in its pocket, else the first for that target; null where the set has none. */
export function structureFor(runId: string | null, primaryTarget: string | null, m: Pick<StructuresManifest, 'structures'>): StructureRecord | null {
  if (runId) {
    const own = m.structures.find((s) => s.serves.includes(runId));
    if (own) return own;
  }
  if (primaryTarget) {
    const forTarget = m.structures.filter((s) => s.target === primaryTarget);
    return forTarget.find((s) => s.ligand.drug) ?? forTarget[0] ?? null;
  }
  return null;
}

const files = () => new SimFiles(defaultSource(), STRUCTURES_DIR);

/** The set's manifest, or null when the directory isn't on this server (the listings say so before any request). */
export async function loadManifest(src: SimFiles = files()): Promise<StructuresManifest | null> {
  const raw = await src.maybeJson('structures.json');
  return raw === null ? null : parseManifest(raw);
}

/** One entry's compact file, checked to be its own. */
export async function loadStructure(entry: StructureRecord, src: SimFiles = files()): Promise<StructureFile> {
  const f = await src.json<StructureFile>(entry.path);
  if (!f || f.schema !== STRUCTURES_SCHEMA || f.id !== entry.id) throw new Error(`${entry.path} isn’t ${entry.id}’s ${STRUCTURES_SCHEMA} file`);
  return f;
}

export interface BuiltMesh {
  id: string;
  geometry: Geometry;
  color: string;
  opacity?: number;
}
export interface Built {
  meshes: BuiltMesh[];
  /** Column-major model → world: upright on the longest axis, left of the brain. */
  pose: Float32Array;
  centreMm: Vec3;
  sizeMm: Vec3;
  counts: { ca: number; vertices: number; triangles: number; dimmed: number };
}

/** A target chain's stretches that aren't the target: segments mapped to another accession (a crystal construct's BRIL or T4
 *  lysozyme fusion, an engineered tag the PDB names by the entry id). Nothing when the target's accessions aren't known. */
export function fusionRanges(chain: Pick<StructureChain, 'target' | 'segments'>, targetAccessions: readonly string[] | undefined): { from: number; to: number }[] {
  if (!chain.target || !targetAccessions?.length) return [];
  return chain.segments.filter((s) => s.accession && !targetAccessions.includes(s.accession)).map((s) => ({ from: s.from, to: s.to }));
}

/** The structure's meshes in millimetres about its Cα centre, and the pose that stands it left of the brain. */
export function build(file: StructureFile, scale = MM_PER_ANGSTROM, targetAccessions?: readonly string[]): Built {
  const all: Vec3[] = file.chains.flatMap((c) => c.ca);
  const { centre, axes } = principalAxes(all);
  const toMm = (p: Vec3): Vec3 => [(p[0] - centre[0]) * scale, (p[1] - centre[1]) * scale, (p[2] - centre[2]) * scale];
  const groups: Record<TubeClass, Geometry[]> = { helix: [], strand: [], coil: [], other: [] };
  let dimmed = 0;
  for (const chain of file.chains) {
    const dim = fusionRanges(chain, targetAccessions);
    for (const d of dim) dimmed += Math.max(0, Math.min(chain.ca.length - 1, d.to) - Math.max(0, d.from) + 1);
    const parts = chainTubes(chain, toMm, scale, dim);
    for (const k of Object.keys(parts) as TubeClass[]) groups[k].push(...(parts[k] ?? []));
  }
  const drug = file.ligands.filter((l) => l.role === 'drug').map((l) => ligandGeometry(l, toMm, scale));
  const others = file.ligands.filter((l) => l.role !== 'drug').map((l) => ligandGeometry(l, toMm, scale));
  const meshes: BuiltMesh[] = [];
  for (const k of ['helix', 'strand', 'coil'] as const) if (groups[k].length) meshes.push({ id: `${MESH_PREFIX}${k}`, geometry: merge(groups[k]), color: LOOK[k] });
  if (groups.other.length) meshes.push({ id: `${MESH_PREFIX}other`, geometry: merge(groups.other), color: LOOK.other, opacity: OTHER_OPACITY });
  if (drug.length) meshes.push({ id: `${MESH_PREFIX}drug`, geometry: merge(drug), color: LOOK.drug });
  if (others.length) meshes.push({ id: `${MESH_PREFIX}ligands`, geometry: merge(others), color: LOOK.ligand });
  // Upright about the origin first, to know the standing extent; then the translation that puts its centre left of the brain.
  const upright = uprightPose(axes, [0, 0, 0]);
  const b = bounds(all.map((p) => transformPoint(upright, toMm(p))));
  const centreMm: Vec3 = [-(BRAIN_HALF_WIDTH_MM + GAP_MM + b.size[0] / 2), PLACE_Y_MM, PLACE_Z_MM];
  const pose = uprightPose(axes, [centreMm[0] - b.centre[0], centreMm[1] - b.centre[1], centreMm[2] - b.centre[2]]);
  let vertices = 0;
  let triangles = 0;
  for (const m of meshes) {
    vertices += m.geometry.positions.length / 3;
    triangles += m.geometry.indices.length / 3;
  }
  return { meshes, pose, centreMm, sizeMm: b.size, counts: { ca: all.length, vertices, triangles, dimmed } };
}

/** The orbit that frames the standing molecule from the camera's current direction: far enough that its longest extent
 *  fits the view's height and width with a margin, whatever the yaw. */
export function frameOrbit(centreMm: Vec3, sizeMm: Vec3, cam: { yawDeg: number; pitchDeg: number; fovYDeg: number; aspect: number }): Orbit {
  const tanY = Math.tan((cam.fovYDeg * Math.PI) / 360);
  const tanX = tanY * Math.max(0.2, cam.aspect);
  const half = Math.max(sizeMm[0], sizeMm[1], sizeMm[2]) / 2;
  const d = Math.max(half / tanY, half / tanX) * 1.3 + half;
  return { targetMm: centreMm, yawDeg: cam.yawDeg, pitchDeg: cam.pitchDeg, distanceMm: Math.max(40, Math.round(d * 10) / 10) };
}

/** The meshes into the viewport, each a reference mesh at the pose; the ids drawn, in order. */
export async function showMolecule(vp: Pick<Viewport, 'send'>, built: Built): Promise<string[]> {
  const drawn: string[] = [];
  const pose = Array.from(built.pose);
  for (const m of built.meshes) {
    const look = m.opacity !== undefined ? { opacity: m.opacity, depthWrite: false } : {};
    await vp.send({ cmd: 'mesh', id: m.id, positions: m.geometry.positions, normals: m.geometry.normals, indices: m.geometry.indices, color: m.color, pose, provenance: 'reference', specular: 0.2, ...look } as unknown as Command);
    drawn.push(m.id);
  }
  return drawn;
}

/** The meshes taken away (a switch off, another run's structure; a load clears them itself). */
export async function clearMolecule(vp: Pick<Viewport, 'send'>, ids: readonly string[]): Promise<void> {
  for (const id of ids) await vp.send({ cmd: 'mesh', id, remove: true } as unknown as Command).catch(() => undefined);
}

export const viewportDrawsMolecule = (vp: LiveViewport | null): boolean => !!vp && drawsMeshes(vp);

export type MoleculeStatus = 'off' | 'loading' | 'on' | 'none' | 'nomesh' | 'error';
export interface MoleculeState {
  status: MoleculeStatus;
  /** The run the structure was chosen for. */
  run: string | null;
  entry: StructureRecord | null;
  file: StructureFile | null;
  why: string | null;
  drawn: string[];
  frame: { centreMm: Vec3; sizeMm: Vec3 } | null;
  counts: Built['counts'] | null;
  /** Whether the camera flew to the molecule, so the switch off brings it back. */
  flew: boolean;
  set(p: Partial<Omit<MoleculeState, 'set'>>): void;
}
export const useMolecule = create<MoleculeState>((set) => ({
  status: 'off',
  run: null,
  entry: null,
  file: null,
  why: null,
  drawn: [],
  frame: null,
  counts: null,
  flew: false,
  set: (p) => set(p),
}));
if (typeof window !== 'undefined') (window as unknown as { __molecule?: typeof useMolecule }).__molecule = useMolecule;

/** The experimental method for a line: the PDB's words made short. */
export function methodLabel(method: string | null | undefined): string {
  const m = (method ?? '').toUpperCase();
  if (m.includes('X-RAY')) return 'X-ray';
  if (m.includes('ELECTRON MICROSCOPY')) return 'cryo-EM';
  if (m.includes('NMR')) return 'NMR';
  return method ?? '';
}

/** "Cao et al. 2022" from an entry's primary citation. */
export function citeShort(entry: Pick<StructureRecord, 'citation'>): string | null {
  const c = entry.citation;
  if (!c) return null;
  const first = c.rcsb_authors?.[0]?.split(',')[0]?.trim();
  const year = c.year ? ` ${c.year}` : '';
  if (!first) return c.year ? String(c.year) : null;
  return `${first}${(c.rcsb_authors?.length ?? 0) > 1 ? ' et al.' : ''}${year}`;
}
